import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import ModalConfirmationContent from '../../../../shared/components/modal-confirmation-content/modal-confirmation-content';
import Modal from '../../../../shared/components/modal/modal';
import { useConfirmationError } from '../../../../shared/hooks/use-confirmation-error';
import { useAppSelector } from '../../../../store/hooks';
import { requestPreviewAlertId } from '../../../request/constants/constants';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useDeleteSignRequestMutation } from '../../api/sign-requests-api';
import { signRequestAlertId, signRequestDeleteModalId } from '../../constants/constants';
import { selectTempSignRequestId, setTempSignRequestId } from '../../store/sign-requests-slice';

const SignRequestDeleteModalContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['sign-requests', 'common']);
  const dispatch = useDispatch();
  const activeSignRequestId = useAppSelector(selectTempSignRequestId);
  const [deleteSignRequest, { isLoading }] = useDeleteSignRequestMutation();
  const { confirmationError, addConfirmationError, resetConfirmationError } = useConfirmationError();

  const closeFormModal = () => {
    dispatch(closeModal(signRequestDeleteModalId));
    dispatch(setTempSignRequestId(null));
    resetConfirmationError();
  };

  const setSignRequestDeleteAlertMessage = (alertId: string) => {
    dispatch(
      setAlert({
        id: alertId,
        type: 'success',
        title: t('signRequestDeleteModal.message.fileDeleted'),
      }),
    );
  };

  const onDeleteSubmit = () => {
    resetConfirmationError();

    if (activeSignRequestId) {
      deleteSignRequest(activeSignRequestId)
        .unwrap()
        .then(() => {
          closeFormModal();
          setSignRequestDeleteAlertMessage(signRequestAlertId);
          setSignRequestDeleteAlertMessage(requestPreviewAlertId);
        })
        .catch(() => addConfirmationError(t('errors.deleteFailed', { ns: 'common' })));
    }
  };

  return (
    <Modal
      title={t('signRequestDeleteModal.title')}
      onCancel={closeFormModal}
      isLoading={isLoading}
      customSubmitText={t('action.delete', { ns: 'common' })}
      onOk={onDeleteSubmit}
      isOpen={isOpen}
      afterClose={afterClose}
    >
      <ModalConfirmationContent
        confirmDeleteText={t('signScanRequest.hint.deleteWarning', { ns: 'common' })}
        confirmationError={confirmationError}
      />
    </Modal>
  );
};

const SignRequestDeleteModal = (): JSX.Element => {
  return <LazyModal modalId={signRequestDeleteModalId} Component={SignRequestDeleteModalContent} />;
};

export default SignRequestDeleteModal;
